
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

interface CollectionTemplate {
  id: string;
  title: string;
  description: string;
  content: string;
  custom_title?: string;
  notes?: string;
}

interface Collection {
  id: string;
  name: string;
  description?: string;
  templates: CollectionTemplate[];
}

export const useUserCollections = () => {
  const { user } = useAuth();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [savedTemplates, setSavedTemplates] = useState<CollectionTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const fetchUserCollections = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch user collections with their prompts
        const { data: collectionsData, error: collectionsError } = await supabase
          .from('prompt_collections')
          .select(`
            id,
            name,
            description,
            collection_prompts(
              saved_prompt_id,
              user_saved_prompts(
                id,
                custom_title,
                notes,
                prompt:prompts(id, title, description, content)
              )
            )
          `)
          .eq('user_id', user.id);

        if (collectionsError) throw collectionsError;

        // Fetch individual saved prompts not in collections
        const { data: savedPromptsData, error: savedError } = await supabase
          .from('user_saved_prompts')
          .select(`
            id,
            custom_title,
            notes,
            prompt:prompts(id, title, description, content)
          `)
          .eq('user_id', user.id)
          .not('id', 'in', `(${collectionsData?.flatMap(c => 
            c.collection_prompts?.map(cp => cp.user_saved_prompts?.id).filter(Boolean)
          ).join(',') || 'null'})`);

        if (savedError) throw savedError;

        // Process collections
        const processedCollections: Collection[] = collectionsData?.map(collection => ({
          id: collection.id,
          name: collection.name,
          description: collection.description,
          templates: collection.collection_prompts?.map(cp => ({
            id: cp.user_saved_prompts?.prompt?.id || '',
            title: cp.user_saved_prompts?.custom_title || cp.user_saved_prompts?.prompt?.title || '',
            description: cp.user_saved_prompts?.prompt?.description || '',
            content: cp.user_saved_prompts?.prompt?.content || '',
            custom_title: cp.user_saved_prompts?.custom_title,
            notes: cp.user_saved_prompts?.notes
          })).filter(t => t.id) || []
        })) || [];

        // Process individual saved prompts
        const processedSavedTemplates: CollectionTemplate[] = savedPromptsData?.map(savedPrompt => ({
          id: savedPrompt.prompt?.id || '',
          title: savedPrompt.custom_title || savedPrompt.prompt?.title || '',
          description: savedPrompt.prompt?.description || '',
          content: savedPrompt.prompt?.content || '',
          custom_title: savedPrompt.custom_title,
          notes: savedPrompt.notes
        })).filter(t => t.id) || [];

        setCollections(processedCollections);
        setSavedTemplates(processedSavedTemplates);
      } catch (err: any) {
        console.error('Error fetching user collections:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserCollections();
  }, [user]);

  return { collections, savedTemplates, isLoading, error };
};
